import { useCallback, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@trinity/chat-components/query-keys';
import { ButtonSet } from '../components/Modal/ButtonSet';
import { Header } from '../components/Modal/Header';
import { Modal } from '../components/Modal/Modal';
import { Button } from '../components/presentation/Button';
import { useChatApiClient } from '../containers/Chat/useApiClient';
import { invalidateProjectTabs } from './useEntityPages';

type UseDeleteQuantAnalysisModalProps = {
  projectId: number | undefined;
  quantAnalysisId: number;
  onSuccess?: () => void;
};

export const useDeleteQuantAnalysisModal = ({
  projectId,
  quantAnalysisId,
  onSuccess,
}: UseDeleteQuantAnalysisModalProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const queryClient = useQueryClient();

  const toggleModal = useCallback(() => setIsOpen(!isOpen), [isOpen]);

  const apiClient = useChatApiClient();

  const mutation = useMutation({
    mutationFn: async () => {
      if (!projectId) {
        throw new Error('Project ID is required');
      }

      return apiClient.quantAnalysis.deleteQuantAnalysis({
        projectId,
        quantAnalysisId,
      });
    },
    onSuccess: () => {
      if (!projectId) return;

      // Invalidate the main project query to refresh tabs and quantAnalyses data
      queryClient.invalidateQueries({
        queryKey: queryKeys.projects.one({ projectId }),
      }).catch(() => undefined);

      // Invalidate project state and quant analysis queries
      queryClient.invalidateQueries({
        queryKey: ['project', projectId],
      }).catch(() => undefined);

      // The analysis is one of the project's tabs.
      invalidateProjectTabs(queryClient, projectId).catch(() => undefined);

      queryClient.invalidateQueries({
        queryKey: queryKeys.projects.quantAnalysis.list({ projectId }),
      }).catch(() => undefined);

      setIsOpen(false);

      // Call the optional success callback (for navigation or other side effects)
      onSuccess?.();
    },
  });

  const handleDelete = useCallback(() => {
    if (projectId) {
      mutation.mutate();
    }
  }, [mutation, projectId]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const DeleteModalComponent = useCallback(() => (
    <Modal
      open={isOpen}
      onClose={handleClose}
    >
      <Header text="Delete Analysis" />

      <div style={{ padding: '24px' }}>
        <div>
          Are you sure you want to delete this analysis?
        </div>
      </div>

      <ButtonSet>
        <Button.Destructive
          onClick={handleClose}
          disabled={mutation.isPending}
        >
          Cancel
        </Button.Destructive>

        <Button.Primary
          onClick={handleDelete}
          disabled={mutation.isPending}
        >
          {mutation.isPending ? 'Deleting...' : 'Confirm'}
        </Button.Primary>
      </ButtonSet>
    </Modal>
  ), [isOpen, handleClose, handleDelete, mutation.isPending]);

  return [toggleModal, DeleteModalComponent] as const;
};
